import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';
import 'yet-another-react-lightbox/styles.css';
import Lightbox from 'yet-another-react-lightbox';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
} from 'react-share';
import { ShareGlyph, ShareBubble } from './ShareIcons';

import { UserContext } from './userContext';
import { Loading } from './Loading';
import { BackDropLoad } from './LoadingBackDrop';
import { ContactForm } from './ContactFormDailog';
import { LoginForm } from './LoginFormDailog';
import { Recommended } from './Recommended';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Pin: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
      <circle cx="12" cy="9" r="3" />
    </svg>
  ),
  Tag: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M20.59 13.41 13 21 3 11V3h8z" />
      <circle cx="7.5" cy="7.5" r="1.2" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  ChevronLeft: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m15 18-6-6 6-6" />
    </svg>
  ),
  ChevronRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m9 18 6-6-6-6" />
    </svg>
  ),
  Expand: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M15 3h6v6M14 10l7-7M9 21H3v-6M10 14l-7 7" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
};

/* ═══════════════════ ProductGallery - custom, cool ═══════════════════ */

function ProductGallery({ images = [], onZoom, alt = '' }) {
  const [active, setActive] = useState(0);
  const [, setDirection] = useState(0);
  const wrapRef = useRef(null);
  const touchStartX = useRef(null);

  const total = images.length;
  const next = useCallback(() => { setDirection(1); setActive((i) => (i + 1) % total); }, [total]);
  const prev = useCallback(() => { setDirection(-1); setActive((i) => (i - 1 + total) % total); }, [total]);

  // Keyboard navigation when gallery is hovered/focused
  useEffect(() => {
    const onKey = (e) => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return; // only if visible
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prev, next]);

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next(); else prev();
    }
    touchStartX.current = null;
  };

  if (total === 0) {
    return (
      <div className="aspect-[5/4] rounded-xl border border-border-subtle bg-surface-overlay flex items-center justify-center">
        <span className="text-[13px] text-fg-tertiary">No image</span>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Main image area */}
      <div
        className="
          group relative overflow-hidden rounded-xl
          aspect-[5/4]
          border border-border-subtle bg-surface-overlay
          cursor-zoom-in
        "
        onClick={() => onZoom && onZoom(active)}
      >
        {/* Layered images crossfade */}
        {images.map((img, i) => {
          const isActive = i === active;
          return (
            <img
              key={i}
              src={img.image_url}
              alt={alt}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`
                absolute inset-0 h-full w-full object-contain
                transition-[opacity,transform] duration-[500ms] ease-out-soft
                ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.02]'}
              `}
            />
          );
        })}

        {/* Subtle vignette + amber wash on hover */}
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        <div
          aria-hidden
          className="
            pointer-events-none absolute inset-0
            opacity-0 transition-opacity duration-500 ease-out-soft
            group-hover:opacity-100
          "
          style={{ background: 'radial-gradient(closest-side at 50% 100%, rgba(251,191,36,0.10), transparent 70%)' }}
        />

        {/* Counter pill - top-right */}
        {total > 1 && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/55 backdrop-blur-md px-2.5 py-1">
            <span className="text-[11px] font-bold tracking-[0.06em] text-amber">{active + 1}</span>
            <span aria-hidden className="text-[10px] text-white/55">/</span>
            <span className="text-[11px] font-medium text-white/85">{total}</span>
          </div>
        )}

        {/* Zoom hint - bottom-right */}
        <div className="
          absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full
          border border-white/15 bg-black/55 backdrop-blur-md px-2 py-1
          opacity-0 translate-y-1
          transition-[opacity,transform] duration-300 ease-out-soft
          group-hover:opacity-100 group-hover:translate-y-0
        ">
          <Icon.Expand className="h-3 w-3 text-white" />
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white">Zoom</span>
        </div>

        {/* Arrow buttons (only when more than one) */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous image"
              className="
                group/btn absolute left-3 top-1/2 -translate-y-1/2
                inline-flex h-10 w-10 items-center justify-center rounded-full
                border border-white/15 bg-black/55 backdrop-blur-md text-white
                opacity-0 -translate-x-1
                transition-[opacity,transform,background-color,border-color,color] duration-300 ease-out-soft
                group-hover:opacity-100 group-hover:translate-x-0
                hover:!bg-amber hover:!border-amber hover:!text-ink
              "
            >
              <Icon.ChevronLeft className="h-4 w-4 transition-transform duration-200 group-hover/btn:-translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next image"
              className="
                group/btn absolute right-3 top-1/2 -translate-y-1/2
                inline-flex h-10 w-10 items-center justify-center rounded-full
                border border-white/15 bg-black/55 backdrop-blur-md text-white
                opacity-0 translate-x-1
                transition-[opacity,transform,background-color,border-color,color] duration-300 ease-out-soft
                group-hover:opacity-100 group-hover:translate-x-0
                hover:!bg-amber hover:!border-amber hover:!text-ink
              "
            >
              <Icon.ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
            </button>
          </>
        )}

        {/* Progress dots - bottom-center */}
        {total > 1 && total <= 8 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActive(i); }}
                  aria-label={`Go to image ${i + 1}`}
                  className="rounded-full transition-all duration-300 ease-out-soft"
                  style={{
                    width: isActive ? 18 : 6,
                    height: 6,
                    backgroundColor: isActive ? '#FBBF24' : 'rgba(255,255,255,0.45)',
                    boxShadow: isActive ? '0 0 8px rgba(251,191,36,0.55)' : 'none',
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="mt-2.5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
          {images.map((img, i) => {
            const isActive = i === active;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { setDirection(i > active ? 1 : -1); setActive(i); }}
                aria-label={`Image ${i + 1}`}
                className={`
                  group/thumb shrink-0 relative h-16 w-16 sm:h-[68px] sm:w-[68px]
                  overflow-hidden rounded-lg
                  border-2
                  transition-[border-color,opacity,transform] duration-200 ease-out-soft
                  ${isActive
                    ? 'border-amber opacity-100 scale-[1.02]'
                    : 'border-border-subtle opacity-65 hover:opacity-100 hover:border-border-strong hover:scale-[1.02]'}
                `}
              >
                <img src={img.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                {isActive && (
                  <span aria-hidden className="absolute inset-0 ring-2 ring-inset ring-amber rounded-md pointer-events-none" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────── Meta row ─────────── */

function MetaRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-border-subtle/70 last:border-b-0">
      <span className="mt-0.5 text-amber shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-fg-tertiary leading-none">{label}</div>
        <div className="mt-0.5 text-[13.5px] text-fg-primary font-medium leading-snug">{value}</div>
      </div>
    </div>
  );
}

/* ═══════════════════ ProdServ ═══════════════════ */

export const ProdServ = () => {
  const { id } = useParams();
  const history = useNavigate();
  const url = window.location.href;
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);
  const [show, setShow] = useState(false);
  const [recommended, setRecommended] = useState([]);

  const [wait, setWait] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    if (!id || isNaN(Number(id))) {
      history('/');
      return;
    }
    Axios.post('/new_apis/get-single-prodserv', { id }, { headers: { key } }).then((res) => {
      if (res.data.status) {
        setData(res.data.data[0]);
        setImages(res.data.images || []);
        setShow(res.data.show);
        setRecommended(res.data.recommended || []);
        setLoading(false);
      } else {
        history('/');
      }
    });
  }, [id, history]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => { /* noop */ });
  };

  const openLightbox = (index) => {
    const rotated = [...images.slice(index), ...images.slice(0, index)];
    setLightboxImages(rotated);
    setLightboxOpen(true);
  };

  if (loading) return <Loading />;

  return (
    <>
      {data && (
        <Helmet>
          <title>{data.product_name} - {data.sub_category_name} | ESAY</title>
        </Helmet>
      )}

      <BackDropLoad wait={wait} />

      <PageContainer maxWidth="lg">

        {/* ═════ Hero ═════ */}
        <article className="relative isolate overflow-hidden rounded-2xl border border-border-subtle bg-surface-paper p-4 sm:p-5 md:p-6 mt-4 mb-5">
          <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 opacity-70"
            style={{ background: 'radial-gradient(700px 320px at 8% -10%, rgba(251,191,36,0.08), transparent 60%)' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Custom gallery */}
            <ProductGallery images={images} onZoom={openLightbox} alt={data.sub_category_name} />

            {lightboxOpen && (
              <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxImages.map((s) => ({ src: s.image_url }))}
              />
            )}

            {/* Info */}
            <div className="flex flex-col">
              {/* Category kicker */}
              <div className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
                <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{data.category_name}</span>
              </div>

              {/* Title */}
              <h1 className="text-[20px] sm:text-[24px] md:text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-fg-primary">
                {data.product_name}
              </h1>

              <div aria-hidden className="mt-2.5 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />

              {/* Meta rows */}
              <div className="mt-4">
                <MetaRow
                  icon={<Icon.Tag className="h-3.5 w-3.5" />}
                  label="Category"
                  value={`${data.category_name} · ${data.sub_category_name}`}
                />
                <MetaRow
                  icon={<Icon.Pin className="h-3.5 w-3.5" />}
                  label="Location"
                  value={data.product_location_text}
                />
              </div>

              {/* Overview */}
              {data.product_short_desc && (
                <div className="mt-4">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">Overview</div>
                  <p className="text-[13.5px] leading-[1.6] text-fg-secondary">{data.product_short_desc}</p>
                </div>
              )}

              {/* CTA */}
              {show && (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => {
                      if (user) setShowContactForm(true);
                      else setShowLoginForm(true);
                    }}
                    className="
                      group inline-flex h-11 items-center justify-center gap-1.5
                      rounded-full px-6
                      text-[12.5px] font-bold uppercase tracking-[0.1em]
                      bg-amber text-ink
                      hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_10px_28px_-8px_rgba(251,191,36,0.55)]
                      active:scale-[0.98]
                      transition-all duration-200 ease-out-soft
                    "
                  >
                    Contact now
                    <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              )}

              {/* Share row */}
              {show && (
                <div className="mt-5 pt-4 border-t border-border-subtle flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.18em] text-fg-tertiary">Share</span>
                  <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
                  <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
                  <TwitterShareButton url={url}><ShareBubble label="X" size="sm"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
                  <RedditShareButton url={url}><ShareBubble label="Reddit" size="sm"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
                  <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
                  <TelegramShareButton url={url}><ShareBubble label="Telegram" size="sm"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
                  <EmailShareButton url={url}><ShareBubble label="Email" size="sm"><ShareGlyph.Email /></ShareBubble></EmailShareButton>

                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? 'Link copied' : 'Copy link'}
                    title={copied ? 'Link copied!' : 'Click to copy the URL'}
                    className={`
                      inline-flex h-9 w-9 items-center justify-center rounded-full
                      border border-border-subtle bg-surface-elevated
                      transition-[background-color,border-color,color,transform] duration-200 ease-out-soft
                      hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                      ${copied ? 'text-amber' : 'text-fg-secondary'}
                    `}
                  >
                    {copied ? <Icon.Check className="h-[15px] w-[15px]" /> : <Icon.Link className="h-[15px] w-[15px]" />}
                  </button>
                </div>
              )}
            </div>
          </div>
        </article>

        {/* ═════ Detailed information ═════ */}
        {data.product_info && (
          <section className="mb-6">
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Detailed information</span>
              <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-amber/50 to-transparent" />
            </div>
            <div
              className="rounded-lg border p-4 sm:p-5"
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(10,10,11,0.08)',
                boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
              }}
            >
              <div
                className="
                  text-[13.5px] leading-[1.65]
                  [&_p]:mb-2.5
                  [&_a]:text-amber-deep [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-amber
                  [&_img]:max-w-full [&_img]:h-auto [&_img]:max-h-[260px] [&_img]:object-contain [&_img]:rounded-md [&_img]:my-2.5
                  [&_h1]:mt-4 [&_h1]:mb-1.5 [&_h1]:text-[18px] [&_h1]:font-bold [&_h1]:text-ink
                  [&_h2]:mt-4 [&_h2]:mb-1.5 [&_h2]:text-[16px] [&_h2]:font-bold [&_h2]:text-ink
                  [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-[15px] [&_h3]:font-bold [&_h3]:text-ink
                  [&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-[13.5px] [&_h4]:font-bold [&_h4]:text-ink
                  [&_strong]:text-ink [&_strong]:font-bold
                  [&_b]:text-ink [&_b]:font-bold
                  [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-1.5
                  [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-1.5
                  [&_li]:my-0.5
                  [&_blockquote]:border-l-[3px] [&_blockquote]:border-amber [&_blockquote]:pl-3 [&_blockquote]:my-2.5 [&_blockquote]:italic [&_blockquote]:text-[#444]
                  [&_table]:w-full [&_table]:my-2.5 [&_table]:border-collapse [&_table]:bg-[#FAFAFA] [&_table]:rounded-md [&_table]:overflow-hidden
                  [&_th]:bg-[#F0F0EE] [&_th]:text-ink [&_th]:font-bold [&_th]:p-2 [&_th]:text-left [&_th]:border [&_th]:border-black/10
                  [&_td]:p-2 [&_td]:text-left [&_td]:border [&_td]:border-black/10
                "
                style={{ color: '#1f1f23' }}
              >
                {parse(data.product_info)}
              </div>
            </div>
          </section>
        )}

        {recommended.length > 0 && <Recommended recs={recommended} />}

        {!user && (
          <LoginForm
            open={showLoginForm}
            showload={() => setWait(true)}
            hideload={() => setWait(false)}
            closeform={() => setShowLoginForm(false)}
            opencontactform={() => setShowContactForm(true)}
          />
        )}
        {user && (
          <ContactForm
            open={showContactForm}
            showload={() => setWait(true)}
            hideload={() => setWait(false)}
            closeform={() => setShowContactForm(false)}
            data={data}
            url="prod"
          />
        )}
      </PageContainer>
    </>
  );
};
